import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthLayout from '../../../components/Auth/AuthLayout';
import LoadingButton from '../../../components/Auth/LoadingButton';

const AdminPaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const transactionId = searchParams.get('transaction_id') || 'TXN-ADMIN-SUCCESS-' + Date.now();
  const amount = searchParams.get('amount') || '99.00';
  const plan = searchParams.get('plan') || 'Admin Pro Monthly';
  const nextBilling = searchParams.get('next_billing') || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString();

  const checkIcon = (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
  );

  return (
    <AuthLayout
      title="Payment Successful!"
      subtitle="Your admin subscription has been activated successfully"
      icon={checkIcon}
      iconBgColor="bg-jet-green-100"
      iconColor="text-jet-green-600"
    >
      <div className="space-y-6">
        {/* Payment Success Details */}
        <div className="bg-jet-green-50 border border-jet-green-200 rounded-md p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-jet-green-900 mb-4">Admin Subscription Activated</h3>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-jet-green-700">Transaction ID:</span>
                <span className="text-jet-green-900 font-mono">{transactionId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-jet-green-700">Plan:</span>
                <span className="text-jet-green-900 font-semibold">{plan}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-jet-green-700">Amount Paid:</span>
                <span className="text-jet-green-900 font-semibold">${amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-jet-green-700">Payment Date:</span>
                <span className="text-jet-green-900">{new Date().toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-jet-green-700">Next Billing:</span>
                <span className="text-jet-green-900">{nextBilling}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Benefits */}
        <div className="bg-jet-blue-50 border border-jet-blue-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-jet-blue-900 mb-3">Your Admin Subscription Includes:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-jet-blue-800">
            <div className="flex items-center">
              <svg className="w-4 h-4 text-jet-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
              Full admin panel access
            </div>
            <div className="flex items-center">
              <svg className="w-4 h-4 text-jet-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
              Advanced analytics
            </div>
            <div className="flex items-center">
              <svg className="w-4 h-4 text-jet-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
              User management tools
            </div>
            <div className="flex items-center">
              <svg className="w-4 h-4 text-jet-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
              Priority support
            </div>
            <div className="flex items-center">
              <svg className="w-4 h-4 text-jet-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
              System monitoring
            </div>
            <div className="flex items-center">
              <svg className="w-4 h-4 text-jet-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
              API access
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4">
          <LoadingButton
            onClick={() => navigate('/admin')}
            variant="primary"
            className="bg-jet-green-600 hover:bg-jet-green-700 text-white"
          >
            Access Admin Panel
          </LoadingButton>

          <LoadingButton
            onClick={() => navigate('/admin/billing/receipt', { 
              state: { transactionId } 
            })}
            variant="secondary"
          >
            Download Receipt
          </LoadingButton>
        </div>

        {/* Email Confirmation */}
        <div className="bg-jet-yellow-50 border border-jet-yellow-200 rounded-md p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-jet-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
            </svg>
            <div>
              <h4 className="text-sm font-medium text-jet-yellow-900 mb-1">Confirmation Email Sent</h4>
              <p className="text-sm text-jet-yellow-800">
                A confirmation email with your receipt and subscription details has been sent to your admin email address.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/admin/users')}
            className="p-4 border border-jet-gray-200 rounded-md hover:border-jet-blue-300 hover:bg-jet-blue-50 transition-colors text-center"
          >
            <div className="text-jet-blue-600 mb-2">
              <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
              </svg>
            </div>
            <div className="text-sm font-medium text-jet-gray-900">Manage Users</div>
          </button>

          <button
            onClick={() => navigate('/admin/analytics')}
            className="p-4 border border-jet-gray-200 rounded-md hover:border-jet-green-300 hover:bg-jet-green-50 transition-colors text-center"
          >
            <div className="text-jet-green-600 mb-2">
              <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
              </svg>
            </div>
            <div className="text-sm font-medium text-jet-gray-900">View Analytics</div>
          </button>

          <button
            onClick={() => navigate('/admin/settings')}
            className="p-4 border border-jet-gray-200 rounded-md hover:border-jet-purple-300 hover:bg-jet-purple-50 transition-colors text-center"
          >
            <div className="text-jet-purple-600 mb-2">
              <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
            </div>
            <div className="text-sm font-medium text-jet-gray-900">System Settings</div>
          </button>
        </div>

        {/* Support Information */}
        <div className="text-center pt-4 border-t border-jet-gray-200">
          <p className="text-sm text-jet-gray-600 mb-2">
            Questions about your subscription?
          </p>
          <button
            onClick={() => navigate('/admin/support')}
            className="text-jet-blue-600 hover:text-jet-blue-700 text-sm font-medium transition-colors"
          >
            Contact Admin Support
          </button>
        </div>

        {/* Security Notice */}
        <div className="bg-jet-gray-50 border border-jet-gray-200 rounded-md p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-jet-gray-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
            </svg>
            <div>
              <h4 className="text-sm font-medium text-jet-gray-900 mb-1">Secure Transaction</h4>
              <p className="text-sm text-jet-gray-700">
                Your payment was processed securely. All admin activities are logged and monitored for security compliance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default AdminPaymentSuccessPage;